import type { AudioService, Cue } from './contracts';

/** Procedural cue service, not character voices or licensed music. No autoplay. */
export function createAudio(): AudioService {
  let context: AudioContext | undefined;
  let gain: GainNode | undefined;
  let muted = true;
  let volume = 0.6;
  const update = () => {
    if (context && gain) gain.gain.setTargetAtTime(muted ? 0 : volume * 0.12, context.currentTime, 0.02);
  };
  return {
    async unlock() {
      if (!context) {
        context = new AudioContext();
        gain = context.createGain();
        gain.gain.value = 0;
        gain.connect(context.destination);
      }
      if (context.state === 'suspended') await context.resume();
      update();
    },
    cue(name: Cue) {
      if (!context || !gain || muted || context.state !== 'running') return;
      const frequencies: Record<Cue, number> = { initialize: 660, snap: 90, web: 420, portal: 150, restore: 880 };
      const oscillator = context.createOscillator();
      const envelope = context.createGain();
      const duration = name === 'snap' ? 1.8 : 0.42;
      oscillator.frequency.setValueAtTime(frequencies[name], context.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(frequencies[name] * (name === 'snap' ? 0.25 : 0.55), context.currentTime + duration * .85);
      envelope.gain.setValueAtTime(0.001, context.currentTime);
      envelope.gain.exponentialRampToValueAtTime(0.7, context.currentTime + 0.02);
      envelope.gain.exponentialRampToValueAtTime(0.001, context.currentTime + duration);
      oscillator.connect(envelope); envelope.connect(gain);
      oscillator.onended = () => { oscillator.disconnect(); envelope.disconnect(); };
      oscillator.start(); oscillator.stop(context.currentTime + duration + .02);
      if (name === 'snap') {
        // Original impact transient and dust tail; no protected audio recording.
        const buffer = context.createBuffer(1, Math.floor(context.sampleRate * .9), context.sampleRate);
        const samples = buffer.getChannelData(0);
        for (let i = 0; i < samples.length; i++) samples[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / samples.length, 3);
        const noise = context.createBufferSource(); noise.buffer = buffer;
        const filter = context.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 2400;
        const transient = context.createGain(); transient.gain.value = .35;
        noise.connect(filter); filter.connect(transient); transient.connect(gain);
        noise.onended = () => { noise.disconnect(); filter.disconnect(); transient.disconnect(); };
        noise.start();
      }
    },
    setMuted(value) { muted = value; update(); },
    setVolume(value) { volume = Math.min(1, Math.max(0, value)); update(); },
    dispose() { void context?.close(); context = undefined; gain = undefined; },
  };
}
